/**
 * Confirm Modal Component
 * Reusable modal for confirming destructive or irreversible actions
 */

import React, { useEffect, useRef } from 'react';

interface PropTypes {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title?: string
    message: string
    loading?: boolean
}

export const ConfirmDeleteModal: React.FC<PropTypes> = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm',
    message,
    loading = false,
}) => {
    const dialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog || !isOpen) {
            return
        }

        dialog.showModal()

        return () => {
            dialog.close()
        }
    }, [isOpen])

    const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
        if (loading) {
            event.preventDefault()
        }
    }

    const handleBackdropClose = () => {
        if (!loading) {
            dialogRef.current?.close()
        }
    }

    if (!isOpen) {
        return null
    }

    return (
        <dialog
            ref={dialogRef}
            className="confirm-delete-modal"
            aria-labelledby="confirm-delete-modal-title"
            onCancel={handleCancel}
            onClose={onClose}
        >
            <button
                type="button"
                className="confirm-delete-modal-backdrop"
                aria-label="Close dialog"
                onClick={handleBackdropClose}
                disabled={loading}
                tabIndex={-1}
            />
            <div
                className="alert-modal-content"
                style={{ borderTop: '4px solid #f39c12' }}
            >
                <div className="alert-modal-header" style={{ backgroundColor: '#fef5e7' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem', color: '#f39c12' }}>⚠</span>
                        <h3 id="confirm-delete-modal-title" style={{ margin: 0, color: '#2c3e50' }}>{title}</h3>
                    </div>
                    <button
                        type="button"
                        className="modal-close"
                        onClick={onClose}
                        disabled={loading}
                        style={{ fontSize: '1.5rem', lineHeight: '1' }}
                    >
                        ×
                    </button>
                </div>
                <div className="alert-modal-body">
                    <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{message}</p>
                </div>
                <div className="alert-modal-footer">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-secondary"
                        style={{ marginRight: '1rem' }}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="btn btn-danger"
                        disabled={loading}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </dialog>
    )
}
